/* eslint-disable i18next/no-literal-string */
import { Options } from '@contentful/rich-text-react-renderer'
import classnames from 'classnames'
import React from 'react'

import { OptionSelectorDS } from '@src/components/blocks/option-selector-ds'
import { SvdOptionSelector } from '@src/components/blocks/option-selector-svd'
import { ProductPropertyDS } from '@src/components/blocks/product-property-ds'
import SiblingProductOptionsDS from '@src/components/blocks/sibling-product-options-ds'
import { shouldUseEnterQuantityLabel } from '@src/components/cart/b2b/utils/buildSizeBreakdown.util'
import { useLocaleContext } from '@src/components/locale/locale.context'
import { LOCALE_STANDARD_FORMAT } from '@src/config/localisation/locale-config.constants'
import { useStatsigDynamicLocaleConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-locale-config.hook'
import { BLOCKS, MARKS } from '@src/quince-site-common/src/constants/contentfulRichTextTypes'
import { QUINCE_BOT_LOCATORS } from '@src/quince-site-common/src/constants/quinceBot'
import { ContentfulSizeChart } from '@src/quince-site-common/src/domains/contentful/ContentfulProduct'
import { MergedSizeChart } from '@src/quince-site-common/src/domains/MergedSizeChart'
import ProductOptionValue, {
  ProductOption,
  ProductOptionDisplayType,
  ProductOptionName,
} from '@src/quince-site-common/src/domains/ProductOption'
import type SvdProduct from '@src/quince-site-common/src/domains/svd/svd-product'
import type {
  SvdEngravingConfig,
  SvdSelection,
  SvdVda,
} from '@src/quince-site-common/src/domains/svd/svd-product.types'
import { getPrimarySwatchKey } from '@src/quince-site-common/src/helpers/svd-cascade-helper'
import { getSvdAllVariantsOnSaleForValue } from '@src/quince-site-common/src/helpers/svd-price-helper'
import { getSanitizedHtml } from '@src/quince-site-common/src/utils/getSanitizedHtml'
import InputOption from '@src/quince-site-pdp/src/atoms/optionSelector/inputOption/InputOption'
import OptionSelectorLegacy from '@src/quince-site-pdp/src/atoms/optionSelector/OptionSelector'
import useProductPageContext from '@src/quince-site-pdp/src/hooks/useProductContext'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { getStandardLocale } from '@src/utils/i18n.utils'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { getIsForcedTextOption } from '../../molecules/optionSelector/productVariantSelector/hooks/useProductVariantSelector.helper'
import { Icon } from '../icon'
import SiblingProductOptionsLegacy from '../siblingProductOptions'

import ErrorStateIcon from './icons/errorState.inline.svg'
import * as dsStyles from './product-form-ds.styles'
import styles from './productForm.module.scss'
import SizeChartButton from './SizeChartButton'
import { ProductOptionProps } from './types'

// @i18n-ignore
export const AIRLINE_COMPATIBILITY_HASH = 'airline-compatibility'

export function shouldShowOnlySizeChart(
  productOptions: ProductOption[] | undefined,
  sizeChart: ContentfulSizeChart | undefined,
  mergedSizeChart: MergedSizeChart[] | undefined,
  hideSizeChart: boolean | undefined,
) {
  const sizeOption = productOptions?.find(({ name }) => name === ProductOptionName.Size)
  const showOnlySizeChart = !hideSizeChart && sizeOption?.isHidden && !!(sizeChart ?? mergedSizeChart)
  return showOnlySizeChart
}

export const ViewFullProductDetails = ({ text }: { text?: string }) => {
  const { setShowFullPDP } = useProductPageContext()

  function handleClick() {
    setShowFullPDP(true)
    window.scrollTo({
      top: 0,
      // @i18n-ignore
      behavior: 'smooth',
    })
  }

  return (
    <div className={styles.viewFullProdWrapper}>
      <button className={styles.btn} onClick={handleClick}>
        {text ?? 'VIEW FULL PRODUCT DETAILS'}
      </button>
    </div>
  )
}

export const KlarnaWrapper = ({
  hidePayLater,
  salePrice,
  festivalOfferPrice,
  wrapperClassname,
}: {
  hidePayLater?: boolean
  salePrice?: number
  festivalOfferPrice?: number
  wrapperClassname?: string
}) => {
  const { locale } = useLocaleContext()
  const localeConfig = useStatsigDynamicLocaleConfig()
  const clientId = localeConfig?.klarnaClientId
  const klarnaRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!klarnaRef.current) return

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            trackEvent(TELEMETRY_EVENT_NAMES.KLARNA_PAYMENTS_MESSAGE_VIEWED, {
              entity_type: 'action',
              entity_id: 'klarna_payments',
            })
            observer.disconnect()
          }
        })
      },
      { threshold: 0.2 },
    )

    observer.observe(klarnaRef.current)

    return () => {
      observer.disconnect()
    }
  }, [])

  const handleKlarnaClick = () => {
    trackEvent(TELEMETRY_EVENT_NAMES.KLARNA_PAYMENTS_LINK_CLICKED, {
      entity_type: 'action',
      entity_id: 'klarna_payments',
    })
  }

  return !hidePayLater && salePrice && clientId ? (
    <div
      ref={klarnaRef}
      className={
        shouldUseDesignSystem()
          ? classnames(wrapperClassname, dsStyles.klarnaWrapperDsClass)
          : classnames(wrapperClassname, styles.container__szmessage, styles.container__szmessage_newDesignV2)
      }
      onClickCapture={handleKlarnaClick}
    >
      <klarna-placement
        // @i18n-ignore
        data-key="credit-promotion-auto-size"
        // @i18n-ignore
        data-locale={getStandardLocale(locale) || LOCALE_STANDARD_FORMAT}
        data-client-id={clientId}
        data-purchase-amount={Math.floor((festivalOfferPrice ?? salePrice) * 100)}
      />
    </div>
  ) : (
    <></>
  )
}

// Self-gating wrapper: route to DS twin behind the DS flag, legacy otherwise.
// Scoped to product form (PDP / QATC / mini-PDP) — other consumers stay on legacy until migrated.
// NOTE: the SVD flow does NOT go through here — it renders via `renderVdaOption` (below) straight from
// `product.vdas`, so there is exactly one SVD render route. This wrapper stays legacy/DS only.
export const OptionSelector = (p: React.ComponentProps<typeof OptionSelectorLegacy>) => {
  return shouldUseDesignSystem() ? <OptionSelectorDS {...p} /> : <OptionSelectorLegacy {...p} />
}

export const SiblingProductOptions = (p: React.ComponentProps<typeof SiblingProductOptionsLegacy>) =>
  shouldUseDesignSystem() ? <SiblingProductOptionsDS {...p} /> : <SiblingProductOptionsLegacy {...p} />

export const documentToReactComponentsTextOptions: Options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_node: any, children: React.ReactNode) => {
      // eslint-disable-next-line react/no-children-prop
      return React.Children.map(children, (child: React.ReactNode, index: number) => {
        if (typeof child === 'string') {
          // eslint-disable-next-line
          return <span key={index} dangerouslySetInnerHTML={{ __html: getSanitizedHtml(child) }} />
        }
        return child
      })
    },
  },
  renderMark: {
    [MARKS.BOLD]: (text: React.ReactNode) => {
      return typeof text === 'string' ? (
        // eslint-disable-next-line
        <span className="font-weight--500" dangerouslySetInnerHTML={{ __html: getSanitizedHtml(text) }} />
      ) : (
        text
      )
    },
  },
}

// @i18n-ignore
const FIRMNESS_DISPLAY_NAME = 'Firmness'
const FIRMNESS_DISPLAY_NAME_FR = 'Fermeté'
// @i18n-ignore
const VALUE_DISPLAY_NAME = 'Value'
const VALUE_DISPLAY_NAME_FR = 'Valeur'

export const renderProductOption = (props: ProductOptionProps) => {
  const {
    selectedOptions,
    onOptionSelect,
    sizeChart,
    mergedSizeChart,
    handleSizeChart,
    hideSizeChart,
    optionNameSelectedValueMap,
    renderSwatchSelectionButton,
    productFormOptionsClass,
    shouldReduceMarginBottom,
    product,
    legendClassName,
    optionSelectorClassName,
    optionDisplayTypesVsClassName,
    t,
    productPurchaseInfo,
    inputOptionError,
    noMarginForSizeOption,
    isBundle,
    isB2BSelfService,
    colorVariantRef,
    sizeVariantRef,
    sizeOptionRef,
    showMoreOption,
    optionLimit,
    onShowMoreClick,
    excessOptionsTextClassName,
    isMiniPDP,
  } = props
  let productOption = props.productOption
  if (getIsForcedTextOption(product?.id as number)) {
    productOption = new ProductOption({
      ...productOption,
      values: productOption.values?.map(v => {
        return { ...v, displayType: ProductOptionDisplayType.TEXT }
      }),
    })
  }
  const poValueDisplayNameMap = productOption.values?.reduce((map: Record<string, string>, option) => {
    map[option.value] = option.displayName ?? option.value
    return map
  }, {})
  // TODO(STRFRNT-2867) - remove this after release
  let optionValues: ProductOptionValue[] = []
  const isCustomizationInputOption =
    productOption.name === ProductOptionName.Customization && productOption.displayType === 'Input'
  const isEngravingInputOption =
    productOption.name === ProductOptionName.Engraving && productOption.displayType === 'Text_box'

  if (isCustomizationInputOption) {
    optionValues = [
      new ProductOptionValue({
        displayType: ProductOptionDisplayType.DEPRECATED_INPUT,
        value: '',
      }),
    ]
  } else if (isEngravingInputOption) {
    optionValues = [
      new ProductOptionValue({
        displayType: ProductOptionDisplayType.INPUT,
        value: '',
        maxCharacterLimit: productOption.maxCharacterLimit,
        minCharacterLimit: productOption.minCharacterLimit,
      }),
    ]
  } else {
    optionValues = productOption.values ?? []
  }

  if (productOption.isHidden) return null

  // Hardcoding required as productOption.Name comes from API and can be used for tracking
  // Firmness productOptionName is color
  // In giftcard options the displayname will be locale based, thus adding the fr-ca display name which will conditionally add a css class
  const isAllRoundOptions = (() => {
    const excludedDisplayNames = [
      FIRMNESS_DISPLAY_NAME,
      VALUE_DISPLAY_NAME,
      VALUE_DISPLAY_NAME_FR,
      FIRMNESS_DISPLAY_NAME_FR,
    ]
    return productOption.name === ProductOptionName.Color && !excludedDisplayNames.includes(productOption.displayName)
  })()
  const optionSelectedValue = optionNameSelectedValueMap[productOption.name]
  const isDS = shouldUseDesignSystem()

  const displayName = productOption.displayName || productOption.name

  const optionDisplayName =
    isB2BSelfService && ProductOptionName.Size === displayName
      ? shouldUseEnterQuantityLabel(optionValues)
        ? `${t('enter')} ${t('quantity')}`
        : `${t('enter')} ${displayName} ${t('&')} ${t('quantity')}`
      : displayName

  const optionTrackingRef =
    productOption.name === ProductOptionName.Color
      ? colorVariantRef
      : productOption.name === ProductOptionName.Size
        ? sizeVariantRef
        : undefined
  const mergedOptionRef = (element: HTMLFieldSetElement | HTMLDivElement | null) => {
    if (optionTrackingRef) {
      ;(optionTrackingRef as React.MutableRefObject<HTMLFieldSetElement | null>).current =
        element as HTMLFieldSetElement | null
    }

    if (productOption.name == ProductOptionName.Size && sizeOptionRef) {
      ;(sizeOptionRef as React.MutableRefObject<HTMLFieldSetElement | null>).current =
        element as HTMLFieldSetElement | null
    }
  }

  const optionSelectorNode = (
    <OptionSelector
      className={
        isDS
          ? optionSelectorClassName // OptionSelectorDS supplies its own row layout + gaps
          : classnames(
              styles.container__options__option,
              optionSelectorClassName,
              styles.container__options__option_adjustedPadding,
              productOption.name !== ProductOptionName.Color && styles.container__options__option_adjustedPadding_size,
              styles.container__options__option_newDesignV2,
            )
      }
      optionValueList={optionValues}
      selectedValue={optionNameSelectedValueMap[productOption.name]}
      onSelect={value => {
        onOptionSelect(productOption.name, value)
      }}
      isAllRoundOptions={isAllRoundOptions}
      product={product}
      selectorKey={QUINCE_BOT_LOCATORS.PDP.FIRST_PRODUCT_OPTIONS}
      selectedOptions={selectedOptions}
      isPDP={true}
      optionDisplayTypesVsClassName={optionDisplayTypesVsClassName}
      renderAfterColorComponent={renderSwatchSelectionButton}
      displayName={productOption.displayName}
      productPurchaseInfo={productPurchaseInfo}
      inputOptionError={inputOptionError}
      isBundle={isBundle}
      optionName={productOption.name}
      isB2BSelfService={isB2BSelfService}
      showMoreOption={showMoreOption}
      optionLimit={optionLimit}
      onShowMoreClick={onShowMoreClick}
      excessOptionsTextClassName={excessOptionsTextClassName}
      isMiniPDP={isMiniPDP}
    />
  )

  if (isDS) {
    return (
      <ProductPropertyDS
        ref={mergedOptionRef}
        key={productOption.name}
        className={classnames(productFormOptionsClass, dsStyles.optionGroupDsClass)}
        headerClassName={legendClassName}
        headerTestId={`legend_${productOption.displayName}`}
        label={
          <>
            {optionDisplayName}
            {/* TODO(STRFRNT-2867) - remove this after release */}
            {productOption.displayType === 'Text Box'
              ? t('var1_v6', { var1: optionNameSelectedValueMap[productOption.name] ?? '' })
              : ''}
          </>
        }
        hideSeparator={productOption.displayType === 'Input' || productOption.displayType === 'Text Box'}
        selectedValue={
          optionSelectedValue && poValueDisplayNameMap?.[optionSelectedValue]
            ? poValueDisplayNameMap[optionSelectedValue]
            : undefined
        }
        action={
          !hideSizeChart && productOption.name === ProductOptionName.Size && (sizeChart ?? mergedSizeChart) ? (
            <SizeChartButton onClick={handleSizeChart} />
          ) : undefined
        }
      >
        {optionSelectorNode}
      </ProductPropertyDS>
    )
  }

  return (
    <fieldset
      ref={mergedOptionRef}
      className={classnames(
        productFormOptionsClass,
        classnames(styles.container__options, styles.container__options_adjustedPadding, {
          [styles.container__options__colorOptionGroup ?? '']: shouldReduceMarginBottom,
          [styles.container__options__noMarginForSizeOption ?? '']: noMarginForSizeOption,
          // B2B self-serve only, size option only: extra space before the size/qty grid.
          // Top margin collapses with the color option's bottom margin so the larger value wins.
          // Constant 32px (px, not rem — legacy html font-size is viewport-relative; see normalize.scss).
          [styles.container__options__b2bSelfServeSizeSpacing ?? '']:
            isB2BSelfService && productOption.name === ProductOptionName.Size,
        }),
      )}
      key={productOption.name}
    >
      <legend
        data-testid={`legend_${productOption.displayName}`}
        className={classnames(styles.container__label, styles.container__label_newPdpDesignV2, legendClassName)}
      >
        <span className={styles.container__label__optionLabel}>
          {optionDisplayName}
          {/* TODO(STRFRNT-2867) - remove this after release */}
          {productOption.displayType === 'Text Box'
            ? t('var1_v6', { var1: optionNameSelectedValueMap[productOption.name] ?? '' })
            : ''}
          {productOption.displayType === 'Input' || productOption.displayType === 'Text Box' ? '' : ': '}
          {optionSelectedValue && poValueDisplayNameMap?.[optionSelectedValue] ? (
            <span className={styles.container__label__selectedValue}>{poValueDisplayNameMap[optionSelectedValue]}</span>
          ) : (
            <span className="sr-only">{t('not_selected')}</span>
          )}
        </span>
        {!hideSizeChart && productOption.name === ProductOptionName.Size && (sizeChart ?? mergedSizeChart) && (
          <SizeChartButton onClick={handleSizeChart} />
        )}
      </legend>
      {optionSelectorNode}
    </fieldset>
  )
}

interface RenderVdaOptionProps {
  vda: SvdVda
  svdSelected: SvdSelection[]
  /** Value keys still reachable after cascade (from svdCascadeHelper). Undefined → show all. */
  availableValueKeys?: string[]
  onOptionSelect: (vdaKey: string, valueKey: string) => void
  /** The SVD product — used to derive the primary-swatch on-sale red-dot. Omit to skip the cue. */
  product?: SvdProduct
  /**
   * Whether the client has mounted. `false` (SSR + first client render) → honour the server `showRedDot`;
   * `true` → re-derive the red-dot from the current promo window (time-based). The caller flips it post-mount.
   */
  hasMounted?: boolean
  /**
   * Rendered directly after this option's selector — used to place the "Order free swatches" button under
   * the primary swatch (David 2026-07-17). Omit for non-primary VDAs.
   */
  renderAfterOption?: () => React.ReactNode
  /**
   * Color grouping (primary swatch only): render just this subset of value keys in the row. The rendered set
   * is `availableValueKeys ∩ valueKeyFilter`. Omit to render the whole VDA (the common single-row case).
   */
  valueKeyFilter?: string[]
  /** Legend label override — the color group's display name for a grouped row. Defaults to `vda.name`. */
  legendLabel?: string
  productFormOptionsClass?: string
  optionSelectorClassName?: string
  legendClassName?: string
  t: (key: string) => string
}

/**
 * SVD counterpart of `renderProductOption`: builds one option row (fieldset + legend + `SvdOptionSelector`)
 * directly from a `SvdVda` and the SVD-native selection — never from `ProductOption`. This is the single
 * SVD render route (the legacy `OptionSelector` is not involved).
 */
export const renderVdaOption = ({
  vda,
  svdSelected,
  availableValueKeys,
  onOptionSelect,
  product,
  hasMounted,
  renderAfterOption,
  valueKeyFilter,
  legendLabel,
  productFormOptionsClass,
  optionSelectorClassName,
  legendClassName,
  t,
}: RenderVdaOptionProps) => {
  const isDS = shouldUseDesignSystem()
  const selectedValueKey = svdSelected.find(selection => selection.vdaKey === vda.key)?.valueKey
  // Grouped row: render only the group's value keys (∩ cascade-available); the legend shows the group name.
  const groupKeys = valueKeyFilter ? new Set(valueKeyFilter) : undefined
  const effectiveAvailableValueKeys = groupKeys
    ? (availableValueKeys ?? vda.values.map(value => value.key)).filter(key => groupKeys.has(key))
    : availableValueKeys
  const legendName = legendLabel ?? vda.name
  // Only surface the selected value on the row that actually contains it (grouped rows).
  const selectedValue =
    groupKeys && !(selectedValueKey !== undefined && groupKeys.has(selectedValueKey))
      ? undefined
      : vda.values.find(value => value.key === selectedValueKey)

  // On-sale red-dot — primary-swatch only (WG 2026-07-17). Promo windows are time-based, so once mounted the
  // client RE-DERIVES it from the current promo window (`getSvdAllVariantsOnSaleForValue`); the server's
  // precomputed `VdaValue.showRedDot` is honoured for the first (SSR) render to keep hydration stable.
  const isPrimarySwatch = product ? getPrimarySwatchKey(product) === vda.key : false
  const redDotValueKeys = isPrimarySwatch
    ? new Set(
        vda.values
          .filter(value =>
            hasMounted && product ? getSvdAllVariantsOnSaleForValue(product, vda.key, value.key) : value.showRedDot,
          )
          .map(value => value.key),
      )
    : undefined

  const svdOptionSelectorNode = (
    <SvdOptionSelector
      vda={vda}
      selectedValueKey={selectedValueKey}
      availableValueKeys={effectiveAvailableValueKeys}
      redDotValueKeys={redDotValueKeys}
      selectorKey={QUINCE_BOT_LOCATORS.PDP.FIRST_PRODUCT_OPTIONS}
      onSelect={value => {
        onOptionSelect(vda.key, value)
      }}
      className={
        isDS
          ? optionSelectorClassName
          : classnames(
              styles.container__options__option,
              optionSelectorClassName,
              styles.container__options__option_adjustedPadding,
              styles.container__options__option_newDesignV2,
            )
      }
    />
  )

  // DS path renders through the ProductProperty block like renderProductOption above; SVD gains the
  // standard space-12 header offset (it previously had none on the DS branch).
  if (isDS) {
    return (
      <ProductPropertyDS
        key={vda.key}
        className={classnames(productFormOptionsClass, dsStyles.optionGroupDsClass)}
        headerClassName={legendClassName}
        label={legendName}
        selectedValue={selectedValue ? selectedValue.localisedValue : undefined}
      >
        {svdOptionSelectorNode}
        {renderAfterOption?.()}
      </ProductPropertyDS>
    )
  }

  return (
    <fieldset
      key={vda.key}
      className={classnames(
        productFormOptionsClass,
        classnames(styles.container__options, styles.container__options_adjustedPadding),
      )}
    >
      <legend
        className={classnames(
          classnames(styles.container__label, styles.container__label_newPdpDesignV2),
          legendClassName,
        )}
      >
        <span className={styles.container__label__optionLabel}>
          {legendName}
          {': '}
          {selectedValue ? (
            <span className={styles.container__label__selectedValue}>{selectedValue.localisedValue}</span>
          ) : (
            <span className="sr-only">{t('not_selected')}</span>
          )}
        </span>
      </legend>
      {svdOptionSelectorNode}
      {renderAfterOption?.()}
    </fieldset>
  )
}

interface RenderVdaOptionProps {
  vda: SvdVda
  svdSelected: SvdSelection[]
  /** Value keys still reachable after cascade (from svdCascadeHelper). Undefined → show all. */
  availableValueKeys?: string[]
  onOptionSelect: (vdaKey: string, valueKey: string) => void
  productFormOptionsClass?: string
  optionSelectorClassName?: string
  legendClassName?: string
  t: (key: string) => string
}

interface RenderSvdEngravingProps {
  engravingConfig: SvdEngravingConfig
  onOptionSelect: (vdaKey: string, valueKey: string) => void
  inputOptionError?: string
  productFormOptionsClass?: string
  legendClassName?: string
}

/**
 * SVD engraving row — a first-class product object (not a VDA), rendered LAST after all VDA rows (David,
 * 2026-07-17). Reuses the shared `InputOption` leaf; its onChange routes through `onOptionSelect('Engraving',…)`,
 * which the wrapper handles SVD-natively (stores the value + validates against `engravingConfig` in the ATC gate).
 */
export const renderSvdEngraving = ({
  engravingConfig,
  onOptionSelect,
  inputOptionError,
  productFormOptionsClass,
  legendClassName,
}: RenderSvdEngravingProps) => (
  <fieldset
    key="svd-engraving"
    className={classnames(
      productFormOptionsClass,
      styles.container__options,
      styles.container__options_adjustedPadding,
    )}
  >
    <legend className={classnames(styles.container__label, styles.container__label_newPdpDesignV2, legendClassName)}>
      <span className={styles.container__label__optionLabel}>{engravingConfig.displayName}</span>
    </legend>
    <InputOption
      onChange={value => {
        onOptionSelect(ProductOptionName.Engraving, value)
      }}
      maxCharacterLimit={engravingConfig.maxCharacterLimit}
      minCharacterLimit={engravingConfig.minCharacterLimit}
      inputOptionError={inputOptionError}
    />
  </fieldset>
)

export const renderErrorMessage = (errorMessage?: string, className?: string) => {
  return (
    <div
      className={classnames(styles.container__error_message, styles.container__error_message_newDesignV2, className)}
    >
      <Icon iconSvg={ErrorStateIcon} />
      {errorMessage}
    </div>
  )
}
/* eslint-enable i18next/no-literal-string */
